import { gql } from '@apollo/client';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isEmpty, isNil } from 'lodash';
import { ChangeEvent, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { Shield } from '~auth/Shield';
import { useAuth } from '~auth/useAuth';
import { CustomerSubscriptionsTableSubscriptionFragment } from '~components/organization/CustomerSubscriptionsTable/__generated__/CustomerSubscriptionsTable.graphql';
import { CustomerSubscriptions } from '~components/organization/CustomerSubscriptionsV2/CustomerSubscriptions';
import { CustomerUsersTable } from '~components/organization/CustomerUsersTable';
import { CustomerUsersTableUserFragment } from '~components/organization/CustomerUsersTable/__generated__/CustomerUsersTable.graphql';
import { useCustomerUsersTable } from '~components/organization/CustomerUsersTable/useCustomerUsersTable';
import {
  customerBaseValidationSchema,
  customerErrorMap,
  toHandle,
} from '~components/organization/utils';
import { AvatarInput, FileUploadProps } from '~components/ui/AvatarInput';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import useGoBack from '~components/useGoBack';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { useFeatureFlag } from '~utils/features';
import { isDefined } from '~utils/types';
import { useS3Upload } from '~utils/useS3Upload';
import {
  useCreateCustomerPageNewQuery,
  useCreateCustomerV2Mutation,
} from './__generated__/Create.graphql';

// Changed subscriptionId (single string) → subscriptionIds (array)
// so it aligns with the CustomerSubscriptions component interface
const schema = z.object({
  file: z.instanceof(File).optional(),
  uploadUrl: z.string().optional(),
  userIds: z.array(z.string()),
  subscriptionIds: z.array(z.string()),
  ...customerBaseValidationSchema,
});

type FormValues = z.infer<typeof schema>;

export function OrganizationCustomersCreatePage() {
  const handleGoBack = useGoBack();
  const toast = useToast();
  const [createCustomer] = useCreateCustomerV2Mutation();
  const { upload } = useS3Upload();
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions } = useAuth();

  const { data, loading, error } = useCreateCustomerPageNewQuery();

  const activeUserId = data?.me.id;
  const defaultUserIds = isDefined(activeUserId) ? [activeUserId] : [];

  const createForm = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      avatarUrl: undefined,
      userIds: defaultUserIds,
      subscriptionIds: [],
    },
    resolver: zodResolver(schema),
  });

  const { isSubmitting, isDirty, isValid } = createForm.formState;
  const canSubmit = !isSubmitting && isValid && isDirty;

  const handleCreateCustomer = async ({
    name,
    handle,
    avatarUrl,
    file,
    uploadUrl,
    userIds,
    subscriptionIds,
  }: FormValues) => {
    if (!isNil(uploadUrl) && !isNil(file)) {
      try {
        await upload(uploadUrl, file);
      } catch (error) {
        toast({
          status: 'error',
          title: t('uploadImageErr'),
          description: fromError(error, 'UploadFile'),
        });
        return;
      }
    }

    try {
      const { data } = await createCustomer({
        variables: {
          input: {
            handle,
            name,
            subscriptionIds: subscriptionIds,
            avatarUrl: avatarUrl ?? null,
            ...(verifyUserPermissions([Permission.FeatureCustomerScopedAccess]) && { userIds }),
          },
        },
      });

      if (isNil(data)) {
        throw new Error('no data received');
      }

      analytics.track('customerCreateComplete');

      toast({
        status: 'success',
        title: t('customerCreated'),
      });

      handleGoBack();
    } catch (error) {
      toast({
        status: 'error',
        title: t('editCustomerErr'),
        description: fromError(error, 'EditCustomer', customerErrorMap),
      });
    }
  };

  // Reset form once async data is available
  useEffect(() => {
    if (loading === false) {
      createForm.reset({
        userIds: defaultUserIds,
        subscriptionIds: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    analytics.track('customerCreateStart');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VerticalTabContent
      title={t('createCustomer')}
      isLoading={loading}
      error={error}
      onGoBack={handleGoBack}
      data={data?.organization}
      hasStickyHeader={true}
      action={{
        label: t('createCustomer'),
        disabled: !canSubmit,
        isLoading: isSubmitting,
        onClick: createForm.handleSubmit(handleCreateCustomer),
      }}
    >
      {(organization) => (
        <CreateCustomerForm
          key={loading ? 'loading' : 'ready'}
          defaultUserIds={defaultUserIds}
          users={organization.users}
          subscriptions={data?.organization.waveSubscriptions ?? []}
          form={createForm}
        />
      )}
    </VerticalTabContent>
  );
}

type CreateCustomerFormProps = {
  defaultUserIds?: string[];
  form: UseFormReturn<FormValues>;
  users: CustomerUsersTableUserFragment[];
  subscriptions: CustomerSubscriptionsTableSubscriptionFragment[];
};

function CreateCustomerForm({
  users,
  form,
  subscriptions,
  defaultUserIds = [],
}: CreateCustomerFormProps) {
  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  const {
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, touchedFields },
  } = form;

  const { ref: nameInputRef, onChange: onNameChange, ...nameInputProps } = register('name');

  // Watch the array — for create, no prior subscriptions exist so default is []
  const currentSubscriptionIds = watch('subscriptionIds');

  const table = useCustomerUsersTable(users, defaultUserIds, isSubscriptionsEnabled);

  function handleAvatarInputChange(uploadData: FileUploadProps) {
    if (isNil(uploadData.file)) {
      setValue('file', undefined, { shouldDirty: true });
    } else {
      setValue('file', uploadData.file, { shouldDirty: true });
    }

    setValue('avatarUrl', uploadData.uploadPayload?.assetUrl);
    setValue('uploadUrl', uploadData.uploadPayload?.uploadUrl);

    clearErrors('avatarUrl');
  }

  const handleAvatarInputError = (error: Error) => {
    setError('avatarUrl', { message: error.message });
  };

  const handleCustomerNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNameChange(event);

    if (!touchedFields.handle) {
      setValue('handle', toHandle(event.target.value));
    }
  };

  // Receives string[] from CustomerSubscriptions — [] for Lite, [id] for Essential
  const handleSubscriptionIdsChange = (subscriptionIds: string[]) => {
    setValue('subscriptionIds', subscriptionIds, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  // Create the handle from the name if the field hasn't been touched
  useEffect(() => {
    const handle = getValues('handle') ?? '';
    const generatedHandle = toHandle(getValues('name') ?? '');

    if (handle !== generatedHandle) {
      setValue('handle', handle, { shouldTouch: true });
    }
  }, [getValues, setValue]);

  // When user selection changes, update the form data
  useEffect(() => {
    const checkedUserIds = Object.keys(table.state.selectedRowIds).sort();
    const selectionChangeUpdatedData = checkedUserIds.length > 0;

    setValue('userIds', checkedUserIds, {
      shouldTouch: true,
      shouldDirty: selectionChangeUpdatedData,
    });
  }, [setValue, table.state.selectedRowIds]);

  return (
    <form>
      <VStack alignItems="flex-start" spacing={10}>
        <FormControl isInvalid={Boolean(errors.avatarUrl)}>
          <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
            {t('uploadCustomerLogo')}
          </Heading>

          <AvatarInput onChange={handleAvatarInputChange} onError={handleAvatarInputError} />
          <FormErrorMessage>{errors.avatarUrl?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
            {t('customerDetails')}
          </Heading>

          <FormControl isInvalid={Boolean(errors.name)} maxWidth="420" mb="8">
            <FormLabel marginRight="0">{t('customerName')}</FormLabel>
            <Input
              placeholder={t('customerPlaceholder')}
              ref={nameInputRef}
              onChange={handleCustomerNameChange}
              {...nameInputProps}
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.handle)} maxWidth="420">
            <FormLabel marginRight="0">{t('customerHandle')}</FormLabel>
            <Input placeholder="my-awesome-customer" {...register('handle')} />
            <FormErrorMessage>{errors.handle?.message}</FormErrorMessage>
          </FormControl>
        </FormControl>

        <Shield requiredPermissions={[Permission.FeatureCustomerScopedAccess]}>
          <FormControl>
            <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
              {t('users')}
            </Heading>
            {isNil(users) || isEmpty(users) ? (
              <>{t('noUsers')}</>
            ) : (
              <CustomerUsersTable table={table} isDisabled={!isSubscriptionsEnabled} />
            )}
          </FormControl>
        </Shield>

        <Shield requiredPermissions={[Permission.WaveSubscriptionUpdate]}>
          <FormControl>
            <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
              {t('subscriptions')}
            </Heading>
            <CustomerSubscriptions
              subscriptions={subscriptions}
              currentSubscriptionIds={currentSubscriptionIds}
              displayCount={0}
              onChange={handleSubscriptionIdsChange}
            />
          </FormControl>
        </Shield>
      </VStack>
    </form>
  );
}

OrganizationCustomersCreatePage.graphql = {
  queries: {
    CreateCustomerPageNew: gql`
      query CreateCustomerPageNew {
        organization {
          users {
            ...CustomerUsersTableUser
          }
          waveSubscriptions(isAssignable: true) {
            ...CustomerSubscriptionsTableSubscription
          }
        }
        me {
          id
        }
      }
    `,
  },
  mutations: {
    CreateCustomerNew: gql`
      mutation CreateCustomerV2($input: CustomerCreateInputV2!) {
        customerCreateNew(input: $input) {
          id
          handle
          name
          avatarUrl
        }
      }
    `,
  },
};
